import { Chart } from 'chart.js/auto';
import { ChartEvent } from 'chart.js/auto';
import { Scenario } from '@scenario/models/scenario.model';
import { UnitRate } from '@scenario/models/unit-rate.model';
import { PriceDatasetsBuilder } from './price-datasets.builder';
import { ChartMarkerConfiguration } from './chart-marker.configuration';
import { DesignSystemColors } from '@configurations/design-system/design-system-colors.enum';
import { getRelativePosition } from 'chart.js/helpers';
import { InsightService } from '@app/services/insight.service';

interface LegendItem {
  datasetIndex?: number;
  text: string;
  strokeStyle: string;
  lineWidth: number;
  fontColor: string;
  hidden: boolean;
}

export class PriceChartBuilder {
  private readonly priceDatasetsBuilder: PriceDatasetsBuilder;
  private chart: Chart;

  constructor(
    private readonly scenarios: Scenario[],
    private readonly unitRate: UnitRate,
    private readonly chartMarkersConfiguration: ChartMarkerConfiguration,
    private readonly insightService: InsightService,
    private readonly historicalUnitRate: UnitRate = unitRate,
  ) {
      this.priceDatasetsBuilder = new PriceDatasetsBuilder(
        this.scenarios,
        this.unitRate,
        chartMarkersConfiguration,
        this.historicalUnitRate,
      );
  }

  build() {

    this.insightService.recordEvent('chartdisplay', { 'selectedScenariosCount': this.scenarios.length });

    this.chart = new Chart('PricesChart', {
      type: 'line',
      data: this.priceDatasetsBuilder.build(),
      plugins: [this.htmlLegendPlugin(this.priceDatasetsBuilder, this.getOrCreateLegendList, this.insightService)],
      options: {
        onClick: (e: ChartEvent) => {
          const canvasPosition = getRelativePosition(e, this.chart);
          const mx = canvasPosition.x;
          const my = canvasPosition.y;
          this.switchHighlightView(mx, my);
        },
        plugins: {
          // @ts-expect-error custom plugin option not in Chart.js types
          htmlLegend: {
            // ID of the container to put the legend in
            containerID: 'legend-container',
            maxHeight: 200,
          },
          tooltip: {
            callbacks: {
              label: (data) => `${((data.raw) as number).toFixed(2)} : ${data.dataset.label}`,
            },
            position: 'nearest',
            usePointStyle: true,
            itemSort: (a, b) => {
              // @ts-expect-error raw is typed as unknown but is a number at runtime
              return b.raw - a.raw;
            },
          },
          legend: {
            display: false,
          }
        },
        interaction: {
          mode:  this.priceDatasetsBuilder.getPointOrIndexMode(),
          intersect: false,
        },
        aspectRatio: 2.5,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding : {
            left: 4,
            right: 4,
            top: 4,
            bottom: 4
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Years',
            },
          },
          y: {
            title: {
              display: true,
              text: `Price (${this.unitRate.to.currency}, ${
                this.unitRate.to.term !== 'NOMINAL'
                  ? this.unitRate.to.realYear
                  : ''
              } ${this.unitRate.to.term.toUpperCase()})`,
            },
          },
        },
      },
    });

    return this.chart;
  }

  private switchHighlightView(mx:number, my:number) {
    if (this.priceDatasetsBuilder.isHighlightEnable()) {
      this.priceDatasetsBuilder.setHighlightEnable(false);
      this.chart.data = this.priceDatasetsBuilder.build();
      // @ts-expect-error interaction mode type mismatch
      this.chart.options.interaction.mode = this.priceDatasetsBuilder.getPointOrIndexMode();
      this.chart.update();
      this.insightService.recordEvent('chartclick', {'wasHighlighted': true, 'isHighlighted': false});
      return;
    }

    const closestDatasetIndex = this.getClosestDatasetIndex(mx, my);

    if (closestDatasetIndex !== undefined) {
      this.priceDatasetsBuilder.setHighlightEnable(true);
      this.chart.data.datasets = this.priceDatasetsBuilder.buildWithHighlight(closestDatasetIndex);
      // @ts-expect-error interaction mode type mismatch
      this.chart.options.interaction.mode = this.priceDatasetsBuilder.getPointOrIndexMode();
      this.chart.update();
      this.insightService.recordEvent('chartclick', {'wasHighlighted': false, 'isHighlighted': true});
      return;
    }

    this.insightService.recordEvent('chartclick', {'wasHighlighted': false, 'isHighlighted': false});
  }

  private getClosestDatasetIndex(mx: number, my: number) {
    let min_distance = Infinity;
    let closestDatasetIndex: number | undefined = undefined;

    for (let j = 0; j < this.chart.data.datasets.length; j++) {

      const meta = this.chart.getDatasetMeta(j);

      for (let i = 0; i < meta.data.length - 1; i++) {
        const x1 = meta.data[i].x;
        const y1 = meta.data[i].y;
        const x2 = meta.data[i + 1].x;
        const y2 = meta.data[i + 1].y;

        const distance = this.calculateDistanceToLine(x1, y1, x2, y2, mx, my);

        if (distance < min_distance) {
          min_distance = distance;
          closestDatasetIndex = j;
        }
      }
    }
    return closestDatasetIndex;
  }

  private calculateDistanceToLine(x1: number, y1: number, x2:number, y2:number, mx:number, my:number) {
    const dx = x2 - x1, dy = y2 - y1, lengthSq = dx * dx + dy * dy;
    if (lengthSq === 0) return Math.sqrt((x1 - mx) ** 2 + (y1 - my) ** 2);

    const t = Math.max(0, Math.min(1, ((mx - x1) * dx + (my - y1) * dy) / lengthSq));
    const projX = x1 + t * dx, projY = y1 + t * dy;
    return Math.sqrt((projX - mx) ** 2 + (projY - my) ** 2);
  }

  private readonly getOrCreateLegendList = (chart: Chart, id: string) => {
    const legendContainer = document.getElementById(id);

    let listContainer = legendContainer?.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.maxHeight = '430px';
      listContainer.style.overflowY = 'scroll';
      listContainer.style.display = 'flex';
      listContainer.style.flexDirection = 'column';
      listContainer.style.margin = '0';
      listContainer.style.marginTop = '10px';
      listContainer.style.padding = '0';
      listContainer.style.fontSize = '12px';

      legendContainer?.appendChild(listContainer);
    }
    return listContainer;
  };

   private readonly htmlLegendPlugin = (priceDatasetsBuilder: PriceDatasetsBuilder, getOrCreateLegentList: (chart: Chart, id: string) => HTMLUListElement | undefined, insightService: InsightService) => ({
    id: 'htmlLegend',
     // eslint-disable-next-line @typescript-eslint/no-explicit-any
     afterUpdate(chart: any, args: any, options: any) {
      const ul = getOrCreateLegentList(chart, options.containerID);

      // Remove old legend items
      while (ul?.firstChild) {
        ul.firstChild.remove();
      }

      // Reuse the built-in legendItems generator
      const items = chart.options.plugins.legend.labels.generateLabels(chart)
        .filter((item: LegendItem) => item.text !== 'Max' && item.text !== 'Min')
        .sort((a: LegendItem, b: LegendItem) => a.text.localeCompare(b.text));

      items.forEach((item: LegendItem) => {
        const datasetIndex = item.datasetIndex;
        const li = document.createElement('li');

        li.style.alignItems = 'center';
        li.style.cursor = 'pointer';
        li.style.display = 'flex';
        li.style.flexDirection = 'row';
        li.style.marginLeft = '10px';
        li.style.marginTop = '2px';

        li.onclick = () => {
          if (datasetIndex !== undefined && !priceDatasetsBuilder.isHighlightEnable()) {
            chart.data.datasets = priceDatasetsBuilder.buildWithHighlight(datasetIndex);
            chart.options.interaction.mode = priceDatasetsBuilder.getPointOrIndexMode();
            chart.update();
            priceDatasetsBuilder.setHighlightEnable(true);
            insightService.recordEvent('chartlegendclick', { 'wasHighlighted': priceDatasetsBuilder.isHighlightEnable(), 'isHighlighted': true });
            return;
          } else {
            chart.data = priceDatasetsBuilder.build();
            chart.options.interaction.mode = priceDatasetsBuilder.getPointOrIndexMode();
            chart.update();
            priceDatasetsBuilder.setHighlightEnable(false);
            insightService.recordEvent('chartlegendclick', { 'wasHighlighted': priceDatasetsBuilder.isHighlightEnable(), 'isHighlighted': false });
            return;
          }
        };

        // Color box
        const boxSpan = document.createElement('span');
        boxSpan.style.background = item.strokeStyle;
        boxSpan.style.borderColor = item.strokeStyle;
        boxSpan.style.borderWidth = item.lineWidth + 'px';
        boxSpan.style.display = 'inline-block';
        boxSpan.style.flexShrink = '0';
        boxSpan.style.height = '20px';
        boxSpan.style.marginRight = '10px';
        boxSpan.style.width = '20px';

        // Text
        const textContainer = document.createElement('p');

        textContainer.style.color = item.fontColor;
        textContainer.style.margin = '0';
        textContainer.style.padding = '0';
        textContainer.style.textDecoration = item.hidden ? 'line-through' : '';
        textContainer.addEventListener('mouseenter', (e) => {
          (e.currentTarget as HTMLElement).style.color = DesignSystemColors.BLUE_470;
        });
        textContainer.addEventListener('mouseleave', (e) => {
          (e.currentTarget as HTMLElement).style.color = item.fontColor;
        });

        const text = document.createTextNode(item.text);
        textContainer.appendChild(text);

        li.appendChild(boxSpan);
        li.appendChild(textContainer);
        ul?.appendChild(li);
      });
    }
  });
}

